import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatusPill, OPERATOR_JOURNEY } from "@/components/Shared";
import AssessmentBooking from "@/components/AssessmentBooking";
import { Button } from "@/components/ui/button";
import { Check, Circle, Lock, MessageSquare, ClipboardList, User } from "lucide-react";

function StepState({ done, current }) {
  if (done) return <span className="h-7 w-7 rounded-full bg-lomond flex items-center justify-center shrink-0"><Check className="h-4 w-4 text-black" /></span>;
  if (current) return <span className="h-7 w-7 rounded-full border-2 border-lomond flex items-center justify-center shrink-0"><span className="h-2.5 w-2.5 bg-lomond rounded-full" /></span>;
  return <span className="h-7 w-7 rounded-full border-2 border-steel flex items-center justify-center shrink-0"><Lock className="h-3 w-3 text-slate-500" /></span>;
}

function ApplicantDashboard({ data }) {
  const { user, assessment, profile, messages, stage_label } = data;
  const submitted = profile?.application_status === "submitted" || profile?.profile_complete;
  const steps = [
    { label: "Apply", done: submitted, current: !submitted, link: "/profile" },
    { label: "Online assessment", done: assessment?.completed, current: submitted && !assessment?.completed, link: "/assessment" },
    { label: "Assessment day", done: user.stage === "approved_trainee", current: user.stage === "invited" },
    { label: "Training Academy", done: user.stage === "approved_trainee" },
  ];
  return (
    <div className="space-y-6" data-testid="applicant-dashboard">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">You're building a career in plant operation</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="applicant-welcome">
          Welcome, {user.first_name}
        </h1>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-sm text-slate-500">Application status:</span>
          <StatusPill stage={user.stage} label={stage_label} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="progress-card">
            <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg mb-5">Your Progress</h2>
            <div className="space-y-4">
              {steps.map((s, i) => (
                <div key={s.label} className="flex items-center gap-4" data-testid={`progress-step-${i}`}>
                  <StepState done={s.done} current={s.current} />
                  <span className={`text-sm ${s.done ? "text-charcoal font-semibold" : s.current ? "text-lomond font-semibold" : "text-slate-500"}`}>
                    {s.label}
                  </span>
                  {s.link && !s.done && (
                    <Link to={s.link} className="ml-auto text-xs text-lomond hover:underline font-semibold uppercase tracking-wide">
                      {s.current ? "Start" : "Open"}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>

          <AssessmentBooking />

          <section className="bg-slate-panel border border-lomond/50 rounded-sm p-6" data-testid="next-step-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">Your next step</p>
            {!submitted ? (
              <>
                <h3 className="font-heading font-bold uppercase text-charcoal text-lg">Finish and submit your application</h3>
                <p className="text-sm text-slate-500 mt-1 mb-4">
                  Your application is {profile?.completion_pct ?? 0}% complete. It saves automatically as you go — submit it to unlock the online aptitude assessment.
                </p>
                <Link to="/profile"><Button className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="next-step-profile-btn">
                  <User className="h-4 w-4 mr-2" /> Continue Application
                </Button></Link>
              </>
            ) : user.stage === "invited" ? (
              <>
                <h3 className="font-heading font-bold uppercase text-charcoal text-lg">You've been invited to an assessment day</h3>
                <p className="text-sm text-slate-500 mt-1">Great news — choose an available date above to book your place. Interview and simulator all happen on the same day.</p>
              </>
            ) : assessment?.completed ? (
              <>
                <h3 className="font-heading font-bold uppercase text-charcoal text-lg">Assessment complete</h3>
                <p className="text-sm text-slate-500 mt-1">Thank you. Lomond is reviewing your application — we'll message you here as soon as there's a decision.</p>
              </>
            ) : (
              <>
                <h3 className="font-heading font-bold uppercase text-charcoal text-lg">Take your online aptitude assessment</h3>
                <p className="text-sm text-slate-500 mt-1 mb-4">
                  Around 30 questions on safety judgement, numeracy and common sense. No plant experience needed — we're testing your potential to learn.
                </p>
                <Link to="/assessment"><Button className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="next-step-assessment-btn">
                  <ClipboardList className="h-4 w-4 mr-2" /> {assessment?.in_progress_id ? "Continue Assessment" : "Start Assessment"}
                </Button></Link>
              </>
            )}
          </section>
        </div>

        <section className="bg-slate-panel border border-steel rounded-sm p-6 h-fit" data-testid="messages-card">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg mb-4 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-lomond" /> Messages
          </h2>
          {messages?.length ? (
            <div className="space-y-4">
              {[...messages].reverse().map((m, i) => (
                <div key={i} className="border-l-2 border-lomond pl-3" data-testid={`message-${i}`}>
                  <p className="text-sm text-slate-700 leading-relaxed">{m.text}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">
                    {m.from} — {new Date(m.at).toLocaleDateString("en-GB")}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No messages yet. Updates about your application and appointments will appear here.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function TraineeOperatorDashboard({ data }) {
  const { user } = data;
  const day = user.trainee_day ?? 0;
  const isOperator = user.role === "operator";
  const [liveHours, setLiveHours] = useState(null);
  useEffect(() => {
    if (user.role === "operator") {
      api.get("/development/me").then((r) => setLiveHours(r.data.seat_hours.total)).catch(() => {});
    }
  }, [user.role]);
  const grade = user.operator_grade;
  const stepDone = (i) => {
    if (isOperator) {
      if (grade === "competent") return true;
      return i <= 4;
    }
    if (i === 0 || i === 1) return true;
    if (i === 2) return day >= 10;
    return false;
  };
  const stepCurrent = (i) => {
    if (isOperator) return grade !== "competent" && i === 5;
    if (i === 2) return day < 10;
    if (i === 3) return day >= 10;
    return false;
  };
  return (
    <div className="space-y-6" data-testid="trainee-dashboard">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Welcome to Lomond Operator Academy</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="trainee-welcome">
          {user.first_name} {user.surname}
        </h1>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-lomond/40 bg-lomond/10 text-lomond" data-testid="trainee-state-badge">
            {isOperator ? (grade === "competent" ? "Lomond Competent Operator" : "Qualified Development Operator") : `Trainee — Day ${day} of 10`}
          </span>
          {isOperator && (
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-steel bg-elevated text-slate-600" data-testid="seat-hours-badge">
              {liveHours ?? user.seat_hours ?? 0} seat hours
            </span>
          )}
        </div>
      </div>

      <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="journey-card">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg mb-5">Your Journey</h2>
        <div className="space-y-4">
          {OPERATOR_JOURNEY.map((label, i) => (
            <div key={label} className="flex items-center gap-4" data-testid={`journey-step-${i}`}>
              <StepState done={stepDone(i)} current={stepCurrent(i)} />
              <span className={`text-sm ${stepDone(i) ? "text-charcoal font-semibold" : stepCurrent(i) ? "text-lomond font-semibold" : "text-slate-500"}`}>
                {label}
              </span>
              {i === 2 && !isOperator && day > 0 && day < 10 && (
                <span className="ml-auto font-mono text-xs text-lomond" data-testid="trainee-day-counter">Day {day}/10</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {!isOperator ? (
        <section className="bg-slate-panel border border-lomond/50 rounded-sm p-6" data-testid="training-camp-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">Lomond Operator Training Camp</p>
          <h3 className="font-heading font-bold uppercase text-charcoal text-lg">Day {Math.max(day, 1)} of 10</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Daily modules, theory quizzes, practical sign-offs and your reflections live in the Training Camp.
          </p>
          <Link to="/training"><Button className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="open-training-camp-btn">Open Training Camp</Button></Link>
        </section>
      ) : (
        <section className="bg-slate-panel border border-lomond/50 rounded-sm p-6" data-testid="development-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">My Development</p>
          <h3 className="font-heading font-bold uppercase text-charcoal text-lg">Workplace onboarding, action plans &amp; live work</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Track your Connecteam onboarding, development action plan, seat hours and competence progress.
          </p>
          <Link to="/development"><Button className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="open-development-btn">Open My Development</Button></Link>
        </section>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && STAFF_ROLES.includes(user.role)) {
      navigate("/staff", { replace: true });
      return;
    }
    api.get("/applicant/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setError("Could not load your dashboard. Please try again."));
  }, [user, navigate]);

  if (error) return <p className="text-red-400" data-testid="dashboard-error">{error}</p>;
  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="dashboard-loading" />;
  return data.user.role === "applicant" ? <ApplicantDashboard data={data} /> : <TraineeOperatorDashboard data={data} />;
}
